// Local recipe matching — searches what the user already has before the app
// ever goes online.
//
// This is NOT a mock and has no SWAP POINT. It runs over the user's own
// library, so it stays exactly as-is when the online search behind
// mocks/searchOnlineRecipes.js is replaced with a real API. It is pure and
// synchronous on purpose: no delay, no fetch, no context.

// Words that carry no dish meaning. Kept deliberately short — over-stripping
// loses real signal ("green" in "thai green curry" matters).
const STOPWORDS = new Set([
  'a', 'an', 'the', 'some', 'any', 'of', 'for', 'with', 'and', 'or', 'to', 'in',
  'on', 'at', 'by', 'as', 'from', 'my', 'me', 'i', 'we', 'you', 'it', 'that',
  'this', 'is', 'are', 'was', 'be', 'has', 'have', 'just', 'want', 'wanna',
  'like', 'make', 'making', 'made', 'cook', 'cooking', 'recipe', 'recipes',
  'dish', 'meal', 'something', 'anything', 'whatever', 'tonight', 'today',
  'please', 'idea', 'ideas',
]);

// Crude singularisation so "tacos" matches "taco". Deliberately naive — this is
// name matching, not linguistics.
function stem(word) {
  if (word.length > 3 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 3 && word.endsWith('es')) return word.slice(0, -2);
  if (word.length > 2 && word.endsWith('s')) return word.slice(0, -1);
  return word;
}

export function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((word) => word.length > 1 && !STOPWORDS.has(word))
    .map(stem);
}

// What a dish is MADE OF, grouped so that two terms drawn from different groups
// cannot describe the same dish. Within a group the terms are synonyms or
// members of the same class and never conflict: salmon IS fish, a prawn IS a
// shrimp, and a tofu dish IS vegetarian.
//
// This list is deliberately short and is NOT a food ontology. It exists to stop
// one specific lie — answering "chicken pad thai" with a vegetarian recipe —
// and it fails OPEN: a term that is not here (duck, paneer, venison) simply
// produces the old behaviour rather than a wrong veto. Add to it when a real
// query gets a contradictory answer, not speculatively.
const EXCLUSIVE_GROUPS = [
  ['chicken', 'poultry'],
  ['turkey'],
  ['duck'],
  ['beef', 'steak'],
  ['pork', 'bacon', 'ham'],
  ['lamb', 'mutton'],
  ['fish', 'salmon', 'tuna', 'cod', 'haddock'],
  ['shrimp', 'prawn'],
  // One group, not four. These are not alternatives to each other.
  ['tofu', 'veggie', 'vegetarian', 'vegan', 'meatless', 'paneer'],
];

// Stemmed at build time, because the tokens this is tested against are stemmed.
const CLASS_OF = new Map(
  EXCLUSIVE_GROUPS.flatMap((group, index) => group.map((term) => [stem(term), index]))
);

function classesIn(tokens) {
  const found = new Set();
  for (const token of tokens) {
    const group = CLASS_OF.get(token);
    if (group !== undefined) found.add(group);
  }
  return found;
}

// True when the request and the dish name each name a main component and share
// none. "Chicken" against "Veggie pad thai" conflicts; "pad thai" against
// "Veggie pad thai" does not, because a request that names no protein is not a
// request AGAINST one. Silence is never treated as a preference.
export function conflicts(queryTokens, nameTokens) {
  const wanted = classesIn(queryTokens);
  if (wanted.size === 0) return false;
  const offered = classesIn(nameTokens);
  if (offered.size === 0) return false;
  for (const group of wanted) {
    if (offered.has(group)) return false;
  }
  return true;
}

export function isUrl(input) {
  return /^https?:\/\/\S+$/i.test(String(input || '').trim());
}

function normalizeUrl(url) {
  return String(url || '').trim().replace(/\/+$/, '').toLowerCase();
}

// A candidate has to clear this to count as a genuine match. Below it we treat
// the library as having nothing suitable and let the caller go online.
export const SUITABLE_RELEVANCE = 0.5;

// Relevance answers the question two ways and takes the better answer:
//
//   query coverage — how much of what the user asked for the name accounts for.
//     Carries short, precise requests: "tacos" fully matches "Weeknight beef
//     tacos", and the extra words in the name are not held against it.
//   name coverage — how much of the dish name the request accounts for.
//     Carries long, conversational requests: "quick veggie stir fry with
//     whatever is in the fridge" is mostly filler, and dividing by every word
//     the user typed would score a genuine match as a miss.
//
// Either signal alone fails a case people actually type, so neither is enough
// on its own. Taking the max stays strict about what counts as a match — a
// stray shared word like "chicken" still clears neither threshold.
//
// Both signals measure OVERLAP, and overlap alone cannot see a contradiction:
// "pad thai chicken" against "Veggie pad thai" shares two words of three and
// scores 0.67 either way, which is how a vegetarian recipe was once returned
// for a chicken request. `conflicts` runs first for that reason — see its note.
export function relevanceOf(query, recipe) {
  if (isUrl(query)) {
    const target = normalizeUrl(recipe.origin?.url);
    return target && target === normalizeUrl(query) ? 1 : 0;
  }

  const queryTokens = tokenize(query);
  const nameTokenList = tokenize(recipe.dishName);
  if (queryTokens.length === 0 || nameTokenList.length === 0) return 0;

  // A veto, not a penalty. "Chicken" and "veggie" are not a wording difference
  // that a high enough word overlap can outweigh — they are different dishes,
  // and no amount of shared "pad thai" makes one the other.
  if (conflicts(queryTokens, nameTokenList)) return 0;

  const nameTokens = new Set(nameTokenList);
  const matched = queryTokens.filter((token) => nameTokens.has(token));
  if (matched.length === 0) return 0;

  const queryCoverage = matched.length / queryTokens.length;
  const nameCoverage = new Set(matched).size / nameTokens.size;
  return Math.max(queryCoverage, nameCoverage);
}

function tierOf({ lastCookedAt, recipe }) {
  if (lastCookedAt) return 2; // cooked before — the strongest reuse signal
  if (recipe.savedAt) return 1; // explicitly saved
  return 0; // merely seen before
}

export function describeMatch(match) {
  if (!match) return null;
  if (match.kind === 'cooked') {
    const when = new Date(match.lastCookedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    return `From your history · cooked ${when}`;
  }
  if (match.kind === 'saved') return 'From your saved recipes';
  if (match.kind === 'library') return 'You looked at this before';
  return null;
}

// Ranks the user's own recipes against a typed idea.
//
// entries: [{ recipe, lastCookedAt }] — lastCookedAt is derived from History by
//   the caller so this stays pure and History stays the owner of that fact.
//
// Returns every scoring candidate, best first. Callers decide where to cut;
// SUITABLE_RELEVANCE is the threshold they should use.
export function matchRecipes(query, entries) {
  const scored = entries
    .map(({ recipe, lastCookedAt = null }) => {
      const relevance = relevanceOf(query, recipe);
      const tier = tierOf({ lastCookedAt, recipe });
      return {
        recipe,
        relevance,
        tier,
        match: {
          kind: lastCookedAt ? 'cooked' : recipe.savedAt ? 'saved' : 'library',
          lastCookedAt,
          savedAt: recipe.savedAt ?? null,
        },
      };
    })
    .filter((candidate) => candidate.relevance > 0);

  // Dish match comes first, always. Relevance is banded so that near-equal
  // matches let the reuse signal decide — otherwise a hair's difference in
  // wording would rank an online-only recipe above one you actually cooked,
  // which is the whole thing Stage 2 exists to prevent.
  const band = (value) => Math.round(value * 20) / 20;

  return scored.sort((a, b) => {
    if (band(b.relevance) !== band(a.relevance)) return band(b.relevance) - band(a.relevance);
    if (b.tier !== a.tier) return b.tier - a.tier;

    const aWhen = a.match.lastCookedAt ?? a.match.savedAt ?? 0;
    const bWhen = b.match.lastCookedAt ?? b.match.savedAt ?? 0;
    if (bWhen !== aWhen) return bWhen - aWhen;

    if (b.relevance !== a.relevance) return b.relevance - a.relevance;

    // Only once the dish matches equally well does simplicity break the tie.
    return a.recipe.ingredients.length - b.recipe.ingredients.length;
  });
}

export function hasSuitableMatch(candidates) {
  return candidates.some((candidate) => candidate.relevance >= SUITABLE_RELEVANCE);
}

export function suitableMatches(candidates) {
  return candidates.filter((candidate) => candidate.relevance >= SUITABLE_RELEVANCE);
}
